import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { CalendarOff, Plus, Check, X, Trash2, Clock, UserCheck, Users as UsersIcon, CalendarDays, Coins, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { useRefresh } from "@/lib/useRefresh";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import RupiahInput from "@/components/RupiahInput";
import { DIVISION_COLOR, fmtDate, fmtDateTime } from "@/lib/constants";
import ReportDownloader from "@/components/leave/ReportDownloader";

const LEAVE_LABEL = { cuti: "Cuti", izin: "Izin", sakit: "Sakit" };
const LEAVE_COLOR = {
  cuti: "bg-sky-50 text-sky-700 border-sky-300",
  izin: "bg-violet-50 text-violet-700 border-violet-300",
  sakit: "bg-rose-50 text-rose-700 border-rose-300",
};

const STATUS_META = {
  pending_delegate: { l: "Menunggu Delegasi", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  pending_hr: { l: "Menunggu Persetujuan HR", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  pending: { l: "Menunggu Persetujuan HR", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  approved: { l: "Disetujui", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  rejected: { l: "Ditolak", cls: "bg-slate-100 text-slate-600 border-slate-300 line-through" },
};

const REVIEWER_ROLE = (div) => (div === "HRGA" ? "HR" : div === "BOD" ? "Director" : div || "");

const canHrReview = (user) => !!user && ["BOD", "HRGA"].includes(user.division);

export default function LeaveRequestsPage({ embedded = false }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  // 149-cw-r159: deep-link `?open=<id>` — scroll ke row + highlight sementara.
  // Leave tidak punya DetailDialog seperti tasks, jadi UX = anchor visual.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});
  const [rows, setRows] = useState([]);
  const [quota, setQuota] = useState(null);
  const [tab, setTab] = useState("all");
  const [openNew, setOpenNew] = useState(false);
  const [reviewing, setReviewing] = useState(null); // { row, stage: 'delegate'|'hr' }
  const [reviewNote, setReviewNote] = useState("");
  const [reviewAction, setReviewAction] = useState("approve");
  const [sickDeductDays, setSickDeductDays] = useState(0);
  const [confirmDel, setConfirmDel] = useState(null);
  const [openRedeem, setOpenRedeem] = useState(false);
  const [redeemDays, setRedeemDays] = useState(1);
  const [redeemNote, setRedeemNote] = useState("");
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());

  // Redemption review state
  const [redemptions, setRedemptions] = useState([]);
  const [redReviewing, setRedReviewing] = useState(null); // { row, stage: 'fat'|'hr', action: 'approve'|'reject' }
  const [redAmount, setRedAmount] = useState("");
  const [redReviewNote, setRedReviewNote] = useState("");
  const [confirmDelRed, setConfirmDelRed] = useState(null);

  const canFatReviewRedemption = !!user && canPerm(user, "leaves", "update");
  const canHrReviewRedemption = !!user && canPerm(user, "leaves", "update");
  const canSeeAllRedemptions = canFatReviewRedemption || canHrReviewRedemption;

  const load = useCallback(async () => {
    try {
      const promises = [
        api.get("/leave-requests"),
        api.get("/leave-requests/my-quota"),
      ];
      promises.push(api.get(canSeeAllRedemptions ? "/leave-requests/redemptions/all" : "/leave-requests/redemptions"));
      const [rowsRes, quotaRes, redRes] = await Promise.all(promises);
      setRows(rowsRes.data);
      setQuota(quotaRes.data);
      setRedemptions(redRes?.data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  }, [canSeeAllRedemptions]);
  useEffect(() => { load(); }, [load]);

  // 149-cw-r159: consume deep-link `?open=<id>` — scroll ke row + highlight
  // 3 detik. Clear param setelah dibaca supaya back-button prediktabel.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || rows.length === 0) return;
    const found = rows.find((r) => r.id === openId);
    if (!found) {
      // Deep-link menunjuk row yg sudah terhapus / di luar scope — beri tahu user.
      toast.info("Item tidak ditemukan — mungkin sudah dihapus atau di luar akses Anda.", { id: "deeplink-not-found" });
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
      return;
    }
    setHighlightId(openId);
    // Scroll ke row (setelah DOM render).
    setTimeout(() => {
      const el = rowRefs.current[openId];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    // Clear query param supaya refresh tidak trigger ulang.
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
    // Auto-clear highlight setelah 3 detik.
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
  }, [searchParams, rows, setSearchParams]);
  useRefresh(load);

  const isPendingDelegate = (r) => r.status === "pending_delegate";
  const isPendingHr = (r) => r.status === "pending_hr" || r.status === "pending";

  const todayISO = new Date().toISOString().slice(0, 10);
  const currentlyOnLeave = rows.filter((r) => r.status === "approved" && r.start_date <= todayISO && r.end_date >= todayISO);

  const myDelegateInbox = rows.filter((r) => r.delegate_id === user?.id && isPendingDelegate(r));
  const myHrInbox = rows.filter((r) => canHrReview(user) && isPendingHr(r) && r.user_id !== user?.id);

  const counts = useMemo(() => ({
    all: rows.length,
    mine: rows.filter((r) => r.user_id === user?.id).length,
    delegate_inbox: myDelegateInbox.length,
    hr_inbox: myHrInbox.length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
  }), [rows, user?.id, myDelegateInbox.length, myHrInbox.length]);

  const visible = useMemo(() => {
    if (tab === "mine") return rows.filter((r) => r.user_id === user?.id);
    if (tab === "delegate_inbox") return myDelegateInbox;
    if (tab === "hr_inbox") return myHrInbox;
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [rows, tab, user?.id, myDelegateInbox, myHrInbox]);

  const submitReview = async () => {
    if (!reviewing) return;
    const { row, stage } = reviewing;
    const endpoint = stage === "delegate"
      ? `/leave-requests/${row.id}/delegate-review`
      : `/leave-requests/${row.id}/review`;
    const payload = { action: reviewAction, note: reviewNote };
    if (stage === "hr" && reviewAction === "approve" && row.leave_type === "sakit") {
      payload.sick_deduct_days = Math.max(0, Math.min(Number(sickDeductDays) || 0, Number(row.days_total) || 0));
    }
    try {
      await api.patch(endpoint, payload);
      toast.success(`Permintaan ${reviewAction === "approve" ? "disetujui" : "ditolak"} pada tahap ${stage === "delegate" ? "delegate" : "HR approval"}`);
      setReviewing(null); setReviewNote(""); setSickDeductDays(0);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/leave-requests/${confirmDel.id}`);
      toast.success("Permintaan dihapus");
      setConfirmDel(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const openRedReview = (row, stage, action) => {
    setRedReviewing({ row, stage, action });
    setRedReviewNote("");
    // Pre-fill amount: FAT uses suggested (base/22); HR pre-fills the amount FAT already set
    if (stage === "fat") {
      setRedAmount(String(row.suggested_amount_per_day || ""));
    } else {
      setRedAmount(String(row.amount_per_day || row.suggested_amount_per_day || ""));
    }
  };

  const submitRedReview = async () => {
    if (!redReviewing) return;
    const { row, stage, action } = redReviewing;
    const endpoint = stage === "fat"
      ? `/leave-requests/redemptions/${row.id}/fat-review`
      : `/leave-requests/redemptions/${row.id}/hr-review`;
    const payload = { action, note: redReviewNote };
    if (action === "approve") {
      const amt = Number(String(redAmount).replace(/[^\d.]/g, ""));
      if (stage === "fat" && (!amt || amt <= 0)) {
        toast.error("Nominal uang per hari wajib diisi dan > 0");
        return;
      }
      if (amt > 0) payload.amount_per_day = amt;
    }
    try {
      await api.patch(endpoint, payload);
      toast.success(`Pencairan cuti ${action === "approve" ? "disetujui" : "ditolak"} pada tahap ${stage === "fat" ? "FAT/BOD" : "HR/BOD"}`);
      setRedReviewing(null); setRedAmount(""); setRedReviewNote("");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const doDeleteRedemption = async () => {
    if (!confirmDelRed) return;
    try {
      await api.delete(`/leave-requests/redemptions/${confirmDelRed.id}`);
      toast.success("Redemption dihapus");
      setConfirmDelRed(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const fmtIDR = (n) => {
    const v = Number(n) || 0;
    return "Rp " + v.toLocaleString("id-ID", { maximumFractionDigits: 0 });
  };

  const redFatInbox = redemptions.filter((r) => canFatReviewRedemption && (r.status === "pending_fat" || r.status === "pending") && r.user_id !== user?.id);
  const redHrInbox = redemptions.filter((r) => canHrReviewRedemption && r.status === "pending_hr" && r.user_id !== user?.id);
  const redPendingCount = redFatInbox.length + redHrInbox.length;

  const tabButtons = [
    { k: "all", l: "Semua", c: counts.all },
    { k: "mine", l: "Milik Saya", c: counts.mine },
    { k: "delegate_inbox", l: "Review Delegasi Saya", c: counts.delegate_inbox, badge: counts.delegate_inbox > 0 },
    ...(canHrReview(user) ? [{ k: "hr_inbox", l: "Review HR/BOD", c: counts.hr_inbox, badge: counts.hr_inbox > 0 }] : []),
    { k: "approved", l: "Disetujui", c: counts.approved },
    { k: "rejected", l: "Ditolak", c: counts.rejected },
  ];

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-4"} data-testid="leaves-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* R232: mode embedded (tab Cuti & Izin di Kehadiran & Perizinan) —
            judul halaman disembunyikan; seluruh fungsi tetap tersedia. */}
        {!embedded && (
        <div>
          <p className="overline text-slate-500">Pengajuan</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A] flex items-center gap-2">
            <CalendarOff size={24} className="text-[#FF5A1F]" /> Cuti & Izin
          </h1>
        </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/leaves/calendar"
            data-testid="btn-open-calendar"
            className="rounded-none border border-slate-300 h-9 px-3 text-xs uppercase tracking-wider font-semibold text-slate-700 hover:border-[#0B1D3A] inline-flex items-center gap-1"
          >
            <CalendarDays size={13} /> Kalender
          </Link>
          {["BOD", "HRGA"].includes(user?.division) && (
            <ReportDownloader reportMonth={reportMonth} setReportMonth={setReportMonth} reportYear={reportYear} setReportYear={setReportYear} />
          )}
          <Button
            data-testid="btn-open-new-leave"
            onClick={() => setOpenNew(true)}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white uppercase text-xs tracking-wider font-semibold h-9"
          >
            <Plus size={14} className="mr-1" /> Pengajuan Baru
          </Button>
        </div>
      </div>

      {/* Quota banner (own quota) */}
      {quota && (
        quota.eligible ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="leave-quota">
            <div className="border border-slate-300 bg-white px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Terakumulasi {quota.year}</div>
              <div className="font-display text-2xl tabular-nums text-[#0B1D3A]" data-testid="quota-accrued">{quota.accrued} <span className="text-[10px] font-normal text-slate-400">hari</span></div>
              <div className="text-[10px] text-slate-400">1 hari / bulan · reset tiap 1 Jan</div>
            </div>
            <div className="border border-slate-300 bg-white px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Terpakai</div>
              <div className="font-display text-2xl tabular-nums text-rose-600" data-testid="quota-used">{quota.used} <span className="text-[10px] font-normal text-slate-400">hari</span></div>
              <div className="text-[10px] text-slate-400">cuti + izin (+ sakit yang dipotong)</div>
            </div>
            <div className="border border-slate-300 bg-white px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Diuangkan (Kumulatif)</div>
              <div className="font-display text-2xl tabular-nums text-amber-600" data-testid="quota-redeemed">{quota.redeemed} <span className="text-[10px] font-normal text-slate-400">hari</span></div>
              <div className="text-[10px] text-slate-400">reset tiap 1 Jan</div>
            </div>
            <div className={`border ${quota.available > 0 ? "border-emerald-500 bg-emerald-50" : "border-rose-500 bg-rose-50"} px-3 py-2 flex flex-col`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Tersedia</div>
              <div className={`font-display text-2xl tabular-nums ${quota.available > 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="quota-available">{quota.available} <span className="text-[10px] font-normal text-slate-400">hari</span></div>
              {quota.available > 0 && (
                <button
                  data-testid="btn-open-redeem"
                  onClick={() => setOpenRedeem(true)}
                  className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline text-left"
                >
                  Uangkan Cuti →
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="border border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-600" data-testid="quota-ineligible">
            <div className="font-semibold text-slate-800">Belum ada jatah cuti</div>
            <div className="mt-0.5">Jatah cuti akan aktif setelah masa kerja mencapai 1 tahun. {quota.work_start_date && <>Masuk kerja sejak <span className="font-semibold">{fmtDate(quota.work_start_date)}</span> — tenure saat ini {quota.tenure_days} hari.</>}</div>
          </div>
        )
      )}

      {/* Currently On Leave banner */}
      <div
        data-testid="on-leave-banner"
        className={`border ${currentlyOnLeave.length > 0 ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-700 border-slate-300"} px-4 py-3 flex flex-wrap items-center gap-4`}
      >
        <div className="flex items-center gap-3 shrink-0">
          <div className={`h-10 w-10 flex items-center justify-center ${currentlyOnLeave.length > 0 ? "bg-[#FF5A1F] text-white" : "bg-slate-100 text-slate-500"} font-display text-2xl tabular-nums`} data-testid="on-leave-count">
            {currentlyOnLeave.length}
          </div>
          <div>
            <div className="overline text-[10px] opacity-70">Sedang Cuti / Izin Hari Ini</div>
            <div className="text-xs opacity-80 tabular-nums">{fmtDate(todayISO)}</div>
          </div>
        </div>
        {currentlyOnLeave.length > 0 && (
          <div className="flex flex-wrap gap-2 flex-1" data-testid="on-leave-list">
            {currentlyOnLeave.map((r) => (
              <span
                key={r.id}
                className="px-2 py-1 text-[10px] bg-white/10 border border-white/20 uppercase tracking-wider"
                title={`${LEAVE_LABEL[r.leave_type]} · ${fmtDate(r.start_date)} → ${fmtDate(r.end_date)}`}
              >
                {r.user_name} · <span className="opacity-70">{LEAVE_LABEL[r.leave_type]}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className={`grid grid-cols-2 md:grid-cols-${tabButtons.length} gap-2`} data-testid="leave-stats">
        {tabButtons.map((s) => (
          <button
            key={s.k}
            data-testid={`leave-tab-${s.k}`}
            onClick={() => setTab(s.k)}
            className={`text-left px-3 py-2 border transition-colors relative ${tab === s.k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}
          >
            <div className="text-[10px] uppercase tracking-wider opacity-70">{s.l}</div>
            <div className="font-display text-2xl tabular-nums">{s.c}</div>
            {s.badge && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-[#FF5A1F] animate-pulse" />}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 overflow-hidden">
        <table className="w-full text-xs" data-testid="leave-table">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">Pemohon</th>
              <th className="text-left px-3 py-2">Jenis</th>
              <th className="text-left px-3 py-2">Periode</th>
              <th className="text-left px-3 py-2">Alasan</th>
              <th className="text-left px-3 py-2">1. Persetujuan Delegasi</th>
              <th className="text-left px-3 py-2">2. Persetujuan HR</th>
              <th className="text-right px-3 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} className="text-center text-slate-400 py-8">Tidak ada permintaan.</td></tr>
            )}
            {visible.map((r) => {
              const isDelegate = r.delegate_id === user?.id && isPendingDelegate(r);
              const isHrReviewable = canHrReview(user) && isPendingHr(r) && r.user_id !== user?.id;
              const deletable = user?.division === "BOD";
              const finalRejected = r.status === "rejected";
              const delegateApproved = r.delegate_status === "approved";
              const delegateRejected = r.delegate_status === "rejected";
              const hrApproved = r.status === "approved";
              const hrRejected = finalRejected && r.delegate_status !== "rejected";
              return (
                <tr key={r.id}
                    ref={(el) => { if (el) rowRefs.current[r.id] = el; else delete rowRefs.current[r.id]; }}
                    className={`border-t border-slate-100 hover:bg-slate-50 transition-colors ${highlightId === r.id ? "bg-amber-50 ring-2 ring-amber-400" : ""}`}
                    data-testid={`leave-row-${r.id}`}>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-slate-800">{r.user_name}</div>
                    <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[r.user_division] || "bg-slate-600 text-white"}`}>{r.user_division}</span>
                      <span className="text-[10px] text-slate-500">Lv{r.user_level} · {r.user_sub_division}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${LEAVE_COLOR[r.leave_type]}`}>{LEAVE_LABEL[r.leave_type]}</span>
                  </td>
                  <td className="px-3 py-2 align-top tabular-nums">
                    <div>{fmtDate(r.start_date)}</div>
                    <div className="text-slate-500">→ {fmtDate(r.end_date)}</div>
                  </td>
                  <td className="px-3 py-2 align-top text-slate-700 max-w-[220px]">
                    <div className="whitespace-pre-wrap">{r.reason}</div>
                  </td>
                  {/* Delegate Approval Column */}
                  <td className="px-3 py-2 align-top" data-testid={`col-delegate-${r.id}`}>
                    {r.delegate_name ? (
                      <>
                        <div className="text-slate-800 font-medium flex items-center gap-1"><UserCheck size={11} className="text-slate-400" />{r.delegate_name}</div>
                        <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                          <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[r.delegate_division] || "bg-slate-600 text-white"}`}>{r.delegate_division}</span>
                          {r.delegate_level && <span className="text-[9px] text-slate-500">Lv{r.delegate_level}</span>}
                        </div>
                        <div className="mt-1">
                          {delegateApproved && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-300"><Check size={11} /> Approved</span>}
                          {delegateRejected && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-rose-50 text-rose-700 border-rose-300"><X size={11} /> Rejected</span>}
                          {r.delegate_status === "pending" && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-amber-50 text-amber-800 border-amber-300"><Clock size={11} /> Menunggu</span>}
                        </div>
                        {r.delegate_review_note && <div className="mt-1 text-[10px] text-slate-500 italic">{r.delegate_review_note}</div>}
                        {r.delegate_reviewed_at && <div className="text-[10px] text-slate-400">{fmtDateTime(r.delegate_reviewed_at)}</div>}
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border bg-slate-50 text-slate-500 border-slate-200 italic">Legacy · N/A</span>
                    )}
                  </td>
                  {/* HR Approval Column */}
                  <td className="px-3 py-2 align-top" data-testid={`col-hr-${r.id}`}>
                    {hrApproved && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-300"><Check size={11} /> Approved</span>
                    )}
                    {hrRejected && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-rose-50 text-rose-700 border-rose-300"><X size={11} /> Rejected</span>
                    )}
                    {(r.status === "pending_hr" || r.status === "pending") && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-blue-50 text-blue-800 border-blue-300"><Clock size={11} /> Menunggu HR Approval</span>
                    )}
                    {r.status === "pending_delegate" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border bg-slate-50 text-slate-500 border-slate-200 italic">Menunggu tahap 1</span>
                    )}
                    {delegateRejected && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border bg-slate-50 text-slate-500 border-slate-200 italic">Dibatalkan</span>
                    )}
                    {r.reviewed_by_name && (
                      <div className="mt-1 text-[10px] text-slate-500">
                        by {r.reviewed_by_name}
                        {(() => {
                          // Best effort: infer reviewer role from the leave record's context if available
                          // We stored reviewed_by but not reviewer's division. Fall back to generic label.
                          return null;
                        })()}
                      </div>
                    )}
                    {r.reviewed_at && (
                      <div className="text-[10px] text-slate-400">{fmtDateTime(r.reviewed_at)}</div>
                    )}
                    {r.review_note && (
                      <div className="mt-1 text-[10px] text-slate-500 italic">{r.review_note}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex justify-end items-center gap-1 flex-wrap">
                      {isDelegate && (
                        <>
                          <button
                            data-testid={`btn-delegate-approve-${r.id}`}
                            onClick={() => { setReviewing({ row: r, stage: "delegate" }); setReviewAction("approve"); setReviewNote(""); }}
                            className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 hover:bg-emerald-50 border border-emerald-300 inline-flex items-center gap-1"
                          ><Check size={12} /> Approve (Delegate)</button>
                          <button
                            data-testid={`btn-delegate-reject-${r.id}`}
                            onClick={() => { setReviewing({ row: r, stage: "delegate" }); setReviewAction("reject"); setReviewNote(""); }}
                            className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700 hover:bg-rose-50 border border-rose-300 inline-flex items-center gap-1"
                          ><X size={12} /> Reject</button>
                        </>
                      )}
                      {isHrReviewable && (
                        <>
                          <button
                            data-testid={`btn-hr-approve-${r.id}`}
                            onClick={() => { setReviewing({ row: r, stage: "hr" }); setReviewAction("approve"); setReviewNote(""); }}
                            className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1"
                          ><Check size={12} /> Approve ({REVIEWER_ROLE(user?.division)})</button>
                          <button
                            data-testid={`btn-hr-reject-${r.id}`}
                            onClick={() => { setReviewing({ row: r, stage: "hr" }); setReviewAction("reject"); setReviewNote(""); }}
                            className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 inline-flex items-center gap-1"
                          ><X size={12} /> Reject</button>
                        </>
                      )}
                      {deletable && (
                        <button
                          data-testid={`btn-delete-leave-${r.id}`}
                          onClick={() => setConfirmDel(r)}
                          className="px-2 py-1 text-[10px] text-slate-500 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
                        ><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NewLeaveDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />

      {/* Redemption Section */}
      <div className="pt-4" data-testid="redemptions-section">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="font-display text-lg text-[#0B1D3A] flex items-center gap-2">
            <Coins size={18} className="text-amber-500" />
            Pencairan Jatah Cuti (Uangkan Cuti)
            {redPendingCount > 0 && (
              <span className="ml-1 text-[10px] px-2 py-0.5 bg-[#FF5A1F] text-white uppercase tracking-wider" data-testid="red-pending-count">
                {redPendingCount} menunggu
              </span>
            )}
          </h2>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">
            Alur 2 tahap: 1) FAT / BOD (input nominal) → 2) HR / BOD
          </div>
        </div>
        <div className="bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-xs" data-testid="redemption-table">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Pemohon</th>
                <th className="text-right px-3 py-2">Hari</th>
                <th className="text-right px-3 py-2">Nominal / Hari</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-left px-3 py-2">1. FAT / BOD</th>
                <th className="text-left px-3 py-2">2. HR Approval</th>
                <th className="text-left px-3 py-2">Catatan</th>
                <th className="text-right px-3 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.length === 0 && (
                <tr><td colSpan={8} className="text-center text-slate-400 py-6">Belum ada pengajuan pencairan cuti.</td></tr>
              )}
              {redemptions.map((r) => {
                const canFatAct = canFatReviewRedemption && (r.status === "pending_fat" || r.status === "pending") && r.user_id !== user?.id;
                const canHrAct = canHrReviewRedemption && r.status === "pending_hr" && r.user_id !== user?.id;
                const amt = r.amount_per_day || 0;
                const days = Number(r.days) || 0;
                const total = amt * days;
                const suggested = r.suggested_amount_per_day || 0;
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`red-row-${r.id}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-slate-800">{r.user_name}</div>
                      <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[r.user_division] || "bg-slate-600 text-white"}`}>{r.user_division}</span>
                        <span className="text-[10px] text-slate-500">{r.user_sub_division}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{fmtDateTime(r.created_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-right align-top tabular-nums font-semibold" data-testid={`red-days-${r.id}`}>{days}</td>
                    <td className="px-3 py-2 text-right align-top tabular-nums" data-testid={`red-amount-${r.id}`}>
                      {amt > 0 ? (
                        <span className="text-slate-800 font-semibold">{fmtIDR(amt)}</span>
                      ) : (
                        <span className="text-slate-400 italic text-[10px]">Blm ditentukan</span>
                      )}
                      {suggested > 0 && amt === 0 && (
                        <div className="text-[9px] text-slate-400">saran: {fmtIDR(suggested)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right align-top tabular-nums font-display" data-testid={`red-total-${r.id}`}>
                      {total > 0 ? fmtIDR(total) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 align-top" data-testid={`red-fat-${r.id}`}>
                      {r.fat_status === "approved" && (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-300"><Check size={11} /> Approved</span>
                          {r.fat_reviewed_by_name && <div className="mt-0.5 text-[10px] text-slate-500">by {r.fat_reviewed_by_name}</div>}
                          {r.fat_reviewed_at && <div className="text-[10px] text-slate-400">{fmtDateTime(r.fat_reviewed_at)}</div>}
                          {r.fat_review_note && <div className="mt-0.5 text-[10px] text-slate-500 italic">{r.fat_review_note}</div>}
                        </>
                      )}
                      {r.fat_status === "rejected" && (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-rose-50 text-rose-700 border-rose-300"><X size={11} /> Rejected</span>
                          {r.fat_reviewed_by_name && <div className="mt-0.5 text-[10px] text-slate-500">by {r.fat_reviewed_by_name}</div>}
                        </>
                      )}
                      {(r.fat_status === "pending" || !r.fat_status) && r.status !== "rejected" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-amber-50 text-amber-800 border-amber-300"><Clock size={11} /> Menunggu FAT/BOD</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top" data-testid={`red-hr-${r.id}`}>
                      {r.hr_status === "approved" && (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-300"><Check size={11} /> Approved</span>
                          {r.hr_reviewed_by_name && <div className="mt-0.5 text-[10px] text-slate-500">by {r.hr_reviewed_by_name}</div>}
                          {r.hr_reviewed_at && <div className="text-[10px] text-slate-400">{fmtDateTime(r.hr_reviewed_at)}</div>}
                          {r.hr_review_note && <div className="mt-0.5 text-[10px] text-slate-500 italic">{r.hr_review_note}</div>}
                        </>
                      )}
                      {r.hr_status === "rejected" && (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-rose-50 text-rose-700 border-rose-300"><X size={11} /> Rejected</span>
                          {r.hr_reviewed_by_name && <div className="mt-0.5 text-[10px] text-slate-500">by {r.hr_reviewed_by_name}</div>}
                        </>
                      )}
                      {r.status === "pending_hr" && (r.hr_status === "pending" || !r.hr_status) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider border bg-blue-50 text-blue-800 border-blue-300"><Clock size={11} /> Menunggu HR/BOD</span>
                      )}
                      {(r.status === "pending_fat" || r.status === "pending") && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border bg-slate-50 text-slate-500 border-slate-200 italic">Menunggu tahap 1</span>
                      )}
                      {r.status === "rejected" && r.fat_status === "rejected" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border bg-slate-50 text-slate-500 border-slate-200 italic">Dibatalkan</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-[10px] text-slate-600 max-w-[180px] whitespace-pre-wrap">{r.note || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex justify-end items-center gap-1 flex-wrap">
                        {canFatAct && (
                          <>
                            <button
                              data-testid={`btn-red-fat-approve-${r.id}`}
                              onClick={() => openRedReview(r, "fat", "approve")}
                              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1"
                            ><Check size={12} /> Approve (FAT)</button>
                            <button
                              data-testid={`btn-red-fat-reject-${r.id}`}
                              onClick={() => openRedReview(r, "fat", "reject")}
                              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700 hover:bg-rose-50 border border-rose-300 inline-flex items-center gap-1"
                            ><X size={12} /> Reject</button>
                          </>
                        )}
                        {canHrAct && (
                          <>
                            <button
                              data-testid={`btn-red-hr-approve-${r.id}`}
                              onClick={() => openRedReview(r, "hr", "approve")}
                              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1"
                            ><Check size={12} /> Approve (HR)</button>
                            <button
                              data-testid={`btn-red-hr-reject-${r.id}`}
                              onClick={() => openRedReview(r, "hr", "reject")}
                              className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-rose-700 hover:bg-rose-50 border border-rose-300 inline-flex items-center gap-1"
                            ><X size={12} /> Reject</button>
                          </>
                        )}
                        {user?.division === "BOD" && (
                          <button
                            data-testid={`btn-red-delete-${r.id}`}
                            onClick={() => setConfirmDelRed(r)}
                            className="px-2 py-1 text-[10px] text-slate-500 hover:text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"
                          ><Trash2 size={12} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Redemption Review Dialog */}
      <Dialog open={!!redReviewing} onOpenChange={(o) => { if (!o) setRedReviewing(null); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="dlg-red-review">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Coins size={18} className="text-amber-500" />
              {redReviewing?.action === "approve" ? "Approve" : "Reject"} — {redReviewing?.stage === "fat" ? "FAT / BOD (Tahap 1)" : "HR / BOD (Tahap 2)"}
            </DialogTitle>
          </DialogHeader>
          {redReviewing?.row && (
            <div className="space-y-3 text-sm">
              <div className="border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="font-semibold text-slate-800">{redReviewing.row.user_name}</div>
                <div className="text-xs text-slate-500">
                  {redReviewing.row.user_division} · {redReviewing.row.user_sub_division}
                </div>
                <div className="mt-1 text-xs text-slate-700">
                  Ingin mencairkan <span className="font-semibold">{redReviewing.row.days} hari</span> cuti
                </div>
                {redReviewing.row.note && (
                  <div className="mt-1 text-[11px] italic text-slate-600">"{redReviewing.row.note}"</div>
                )}
              </div>

              {redReviewing.action === "approve" && (
                <div className={`border px-3 py-2 ${redReviewing.stage === "fat" ? "border-amber-300 bg-amber-50" : "border-blue-300 bg-blue-50"}`}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold mb-1">
                    {redReviewing.stage === "fat" ? "Nominal Uang per Hari (wajib)" : "Nominal Uang per Hari (dapat disesuaikan)"}
                  </div>
                  <div className="text-[10px] text-slate-600 mb-2">
                    {redReviewing.stage === "fat"
                      ? `Saran otomatis dari gaji: ${fmtIDR(redReviewing.row.suggested_amount_per_day || 0)}. Anda dapat mengubah.`
                      : `FAT/BOD sudah menetapkan ${fmtIDR(redReviewing.row.amount_per_day || 0)}. HR/BOD dapat merevisi jika perlu.`}
                  </div>
                  <RupiahInput
                    testid="input-red-amount"
                    value={redAmount}
                    onChange={(v) => setRedAmount(v)}
                    className="h-9 text-sm border border-slate-300 w-full"
                  />
                  <div className="mt-2 text-xs">
                    Total dicairkan: <span className="font-display text-base text-emerald-700">{fmtIDR((Number(String(redAmount).replace(/[^\d.]/g, "")) || 0) * (Number(redReviewing.row.days) || 0))}</span>
                    <span className="text-slate-500"> = {redReviewing.row.days} hari × nominal</span>
                  </div>
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500">Note (opsional)</label>
                <Textarea
                  data-testid="input-red-review-note"
                  value={redReviewNote}
                  onChange={(e) => setRedReviewNote(e.target.value)}
                  className="rounded-none mt-1 text-sm"
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedReviewing(null)} className="rounded-none">Batal</Button>
            <Button
              data-testid="btn-confirm-red-review"
              onClick={submitRedReview}
              className={`rounded-none text-white ${redReviewing?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
            >
              {redReviewing?.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelRed}
        onOpenChange={(o) => { if (!o) setConfirmDelRed(null); }}
        title="Hapus pengajuan pencairan?"
        description="Data pencairan cuti akan dihapus permanen."
        confirmLabel="Hapus"
        onConfirm={doDeleteRedemption}
      />

      <Dialog open={!!reviewing} onOpenChange={(o) => { if (!o) setReviewing(null); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="dlg-leave-review">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {reviewAction === "approve" ? "Approve" : "Reject"} — {reviewing?.stage === "delegate" ? "Delegate Stage" : "HR/BOD Stage"}
            </DialogTitle>
          </DialogHeader>
          {reviewing?.row && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="font-semibold text-slate-800">{reviewing.row.user_name}</div>
                <div className="text-xs text-slate-500">{LEAVE_LABEL[reviewing.row.leave_type]} · {fmtDate(reviewing.row.start_date)} → {fmtDate(reviewing.row.end_date)} · <span className="font-semibold">{reviewing.row.days_total || 0} hari</span></div>
                <div className="mt-2 text-xs text-slate-700 whitespace-pre-wrap">{reviewing.row.reason}</div>
                {reviewing.stage === "hr" && reviewing.row.delegate_review_note && (
                  <div className="mt-2 border-l-2 border-emerald-400 pl-2 text-[10px] text-emerald-700 italic">
                    Delegate ({reviewing.row.delegate_name}): {reviewing.row.delegate_review_note}
                  </div>
                )}
              </div>

              {/* Sick deduction picker for HR/BOD stage approving a sakit leave */}
              {reviewing.stage === "hr" && reviewAction === "approve" && reviewing.row.leave_type === "sakit" && (
                <div className="border border-amber-300 bg-amber-50 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Potongan Jatah Cuti (Sakit)</div>
                  <div className="text-[10px] text-amber-900 mb-2">Pilih berapa hari sakit ini dipotong dari jatah cuti. 0 = tidak dipotong.</div>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="input-sick-deduct-days"
                      type="number"
                      min="0"
                      max={reviewing.row.days_total || 0}
                      value={sickDeductDays}
                      onChange={(e) => setSickDeductDays(e.target.value)}
                      className="rounded-none h-9 text-sm px-2 border border-slate-300 tabular-nums w-24"
                    />
                    <span className="text-xs text-slate-600">dari {reviewing.row.days_total || 0} hari</span>
                    <div className="ml-auto flex gap-1">
                      <button type="button" onClick={() => setSickDeductDays(0)} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-slate-300 hover:bg-white">Tidak Potong</button>
                      <button type="button" onClick={() => setSickDeductDays(reviewing.row.days_total || 0)} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-slate-300 hover:bg-white">Potong Penuh</button>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500">Note (opsional)</label>
                <Textarea
                  data-testid="input-review-note"
                  value={reviewNote}
                  onChange={(e) => setReviewNote(e.target.value)}
                  className="rounded-none mt-1 text-sm"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewing(null)} className="rounded-none">Batal</Button>
            <Button
              data-testid="btn-confirm-review"
              onClick={submitReview}
              className={`rounded-none text-white ${reviewAction === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
            >
              {reviewAction === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => { if (!o) setConfirmDel(null); }}
        title="Hapus permintaan?"
        description="Permintaan cuti/izin akan dihapus permanen."
        confirmLabel="Hapus"
        onConfirm={doDelete}
      />

      {/* Redeem Cuti Dialog */}
      <Dialog open={openRedeem} onOpenChange={setOpenRedeem}>
        <DialogContent className="rounded-none max-w-md" data-testid="dlg-redeem">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Coins size={20} className="text-amber-500" /> Uangkan Jatah Cuti</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600">Konversi jatah cuti tersedia menjadi uang. Tersedia: <span className="font-semibold text-emerald-700">{quota?.available || 0} hari</span></div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Jumlah Hari</label>
              <input
                data-testid="input-redeem-days"
                type="number"
                min="1"
                max={quota?.available || 0}
                value={redeemDays}
                onChange={(e) => setRedeemDays(e.target.value)}
                className="rounded-none h-9 text-sm px-2 border border-slate-300 tabular-nums w-full mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Catatan (opsional)</label>
              <Textarea data-testid="input-redeem-note" value={redeemNote} onChange={(e) => setRedeemNote(e.target.value)} className="rounded-none mt-1 text-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRedeem(false)} className="rounded-none">Batal</Button>
            <Button
              data-testid="btn-confirm-redeem"
              onClick={async () => {
                try {
                  await api.post("/leave-requests/redeem", { days: Number(redeemDays), note: redeemNote });
                  toast.success(`${redeemDays} hari diajukan untuk pencairan. Menunggu persetujuan FAT/BOD lalu HR/BOD.`);
                  setOpenRedeem(false); setRedeemDays(1); setRedeemNote("");
                  await load();
                } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
              }}
              className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white"
            >
              Uangkan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ReportDownloader extracted to /components/leave/ReportDownloader.jsx (session 149-c).

function NewLeaveDialog({ open, onOpenChange, onCreated }) {
  const { user } = useAuth();
  const isBod = user?.division === "BOD";
  const [type, setType] = useState("cuti");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [submissionDate, setSubmissionDate] = useState("");
  const [reason, setReason] = useState("");
  const [delegates, setDelegates] = useState([]);
  const [delegateId, setDelegateId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType("cuti"); setStart(""); setEnd(""); setSubmissionDate(""); setReason(""); setDelegateId("");
    (async () => {
      try {
        const { data } = await api.get("/leave-requests/delegate-candidates");
        setDelegates(data);
      } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    })();
  }, [open]);

  const groupedDelegates = useMemo(() => {
    const bod = delegates.filter((u) => u.division === "BOD");
    const others = delegates.filter((u) => u.division !== "BOD");
    return { bod, others };
  }, [delegates]);

  const submit = async (opts = {}) => {
    if (!start || !end) { toast.error("Tanggal wajib diisi"); return; }
    if (end < start) { toast.error("Tanggal selesai harus setelah tanggal mulai"); return; }
    if (!reason.trim()) { toast.error("Alasan wajib diisi"); return; }
    if (!delegateId) { toast.error("Pilih delegate yang akan menerima tugas"); return; }
    setLoading(true);
    try {
      const url = opts.force ? "/leave-requests?force_overlap=true" : "/leave-requests";
      const body = { leave_type: type, start_date: start, end_date: end, reason, delegate_id: delegateId };
      // BOD only: kirim submission_date kalau di-set (backdating pengajuan yang telat masuk).
      if (isBod && submissionDate) body.submission_date = submissionDate;
      await api.post(url, body);
      toast.success("Permintaan diajukan. Menunggu persetujuan delegate.");
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      const dtl = e.response?.data?.detail;
      // Overlap warning: FastAPI 409 with structured detail. Ask user to confirm override.
      if (e.response?.status === 409 && dtl && typeof dtl === "object" && dtl.code === "OVERLAP_WARNING") {
        const proceed = window.confirm(`${dtl.message}\n\nLanjutkan pengajuan?`);
        if (proceed) return submit({ force: true });
        setLoading(false);
        return;
      }
      toast.error(formatDetail(dtl));
    }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-md" data-testid="dlg-new-leave">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Pengajuan Cuti & Izin Baru</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Jenis</label>
            <div className="mt-1 grid grid-cols-3 gap-1">
              {["cuti", "izin", "sakit"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`btn-leave-type-${t}`}
                  onClick={() => setType(t)}
                  className={`text-xs px-2 py-2 border font-semibold uppercase tracking-wider ${type === t ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-700 border-slate-300 hover:border-[#0B1D3A]"}`}
                >
                  {LEAVE_LABEL[t]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Mulai *</label>
              <DatePicker value={start} onChange={setStart} testid="dp-leave-start" placeholder="Pilih…" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Selesai *</label>
              <DatePicker value={end} onChange={setEnd} testid="dp-leave-end" placeholder="Pilih…" />
            </div>
          </div>
          {isBod && (
            <div className="border-l-2 border-amber-400 bg-amber-50/50 pl-3 py-2" data-testid="bod-submission-date-block">
              <label className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold flex items-center gap-1">
                Tanggal Pengajuan
                <span className="text-[9px] text-amber-600 normal-case tracking-normal font-normal italic">(BOD only — untuk backdate pengajuan lisan yang telat masuk sistem)</span>
              </label>
              <DatePicker value={submissionDate} onChange={setSubmissionDate} testid="dp-leave-submission" placeholder="Kosongkan = pakai hari ini" />
              <div className="mt-1 text-[10px] text-amber-700/80">
                {submissionDate ? `Pengajuan akan dicatat pada ${submissionDate}` : "Default: hari ini"}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <UsersIcon size={11} /> Tugas Dibebankan Kepada *
            </label>
            <Select value={delegateId} onValueChange={setDelegateId}>
              <SelectTrigger data-testid="select-delegate" className="rounded-none h-9 mt-1 text-xs">
                <SelectValue placeholder={delegates.length === 0 ? "Tidak ada delegate tersedia" : "Pilih delegate…"} />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                {groupedDelegates.bod.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-slate-400 font-semibold">BOD</div>
                    {groupedDelegates.bod.map((u) => (
                      <SelectItem key={u.id} value={u.id} className="text-xs">
                        {u.name} <span className="text-slate-400 ml-1">· Lv{u.level}</span>
                      </SelectItem>
                    ))}
                  </>
                )}
                {groupedDelegates.others.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-slate-400 font-semibold mt-1">Divisi Anda</div>
                    {groupedDelegates.others.map((u) => (
                      <SelectItem key={u.id} value={u.id} className="text-xs">
                        {u.name} <span className="text-slate-400 ml-1">· Lv{u.level} · {u.sub_division}</span>
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            <div className="mt-1 text-[10px] text-slate-500">Delegate akan menerima notifikasi dan wajib menyetujui sebelum HR Approval.</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Alasan *</label>
            <Textarea
              data-testid="input-leave-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="rounded-none mt-1 text-sm"
              rows={3}
              placeholder="Jelaskan alasan…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Batal</Button>
          <Button
            data-testid="btn-submit-leave"
            onClick={submit}
            disabled={loading}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white"
          >
            {loading ? "Menyimpan…" : "Ajukan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
